import { ref, unref, computed, readonly } from "vue";
import { apolloWatchQuery } from "../../apollo-client/helper";
import useFacility from "../authentication/useFacility";
import {
  deviceLastMotion,
  extractDevicesFields,
  formatDeviceName,
  unitWithAvailableDevices,
} from "../common/helpers/devices";
import { demoResponder, isActiveDemoUnit } from "../renters/helper";
import {
  INVENTORY_SMART_UNITS,
  ENDED_SERVICE_SMART_UNITS,
  ORGANIZATION_DEVICES,
  OFFICE_INVENTORY_DEVICES,
  PREINSTALLED_INVENTORY_UNITS,
  UNAVAILABLE_INVENTORY,
} from "./graphql";
import { stringSortCollator } from "@tod-ui/helpers/strings";
import { displayUnitType } from "@tod-ui/helpers/unitType";

const inventoryUnits = ref([]);
const endedServiceUnits = ref([]);
const facilityDevices = ref([]);
const officeInventoryDevices = ref([]);
const preinstalledInventoryUnits = ref([]);
const unavailableInventoryItems = ref([]);

const devicesLoading = ref(true);
const unitsLoading = ref(true);
const endedServiceUnitsLoading = ref(true);
const officeInventoryLoading = ref(true);
const preinstalledInventoryLoading = ref(true);
const unavailableInventoryLoading = ref(true);

const officeInventoryLoaded = ref(false);
const preinstalledInventoryLoaded = ref(false);
const unavailableInventoryLoaded = ref(false);

const createPaginationState = () => ({
  currentPage: 1,
  totalPages: 1,
  totalCount: 0,
  pageSize: 10,
  hasNextPage: false,
  hasPreviousPage: false,
  hasMore: { newer: false, older: false },
  historyStack: [],
});

const officePaginationTracking = ref(createPaginationState());
const preinstalledPaginationTracking = ref(createPaginationState());
const unavailablePaginationTracking = ref(createPaginationState());

const officeItemsPerPage = ref(10);
const preinstalledItemsPerPage = ref(10);
const unavailableItemsPerPage = ref(10);

const currentTab = ref(0);

let devicesObservable = null;
let unitsObservable = null;
let endedServiceObservable = null;
let officeInventoryObservable = null;
let preinstalledInventoryObservable = null;
let unavailableInventoryObservable = null;
const originalTotalCounts = ref({
  0: 0,
  1: 0,
  2: 0,
});

const TAB_CONFIGS = {
  0: {
    itemsPerPage: officeItemsPerPage,
    loading: officeInventoryLoading,
    loaded: officeInventoryLoaded,
    observable: () => officeInventoryObservable,
    pagination: officePaginationTracking,
    query: OFFICE_INVENTORY_DEVICES,
    targetArray: officeInventoryDevices,
    dataKey: "organizationDevicesPaginated",
    queryVariables: (facilityId) => ({
      id: facilityId,
      availableInventory: true,
      dispositions: ["in_service", "waiting"],
      statuses: ["ok"],
      organizationAssigned: false,
    }),
  },
  1: {
    itemsPerPage: preinstalledItemsPerPage,
    loading: preinstalledInventoryLoading,
    loaded: preinstalledInventoryLoaded,
    observable: () => preinstalledInventoryObservable,
    pagination: preinstalledPaginationTracking,
    query: PREINSTALLED_INVENTORY_UNITS,
    targetArray: preinstalledInventoryUnits,
    dataKey: "organizationDescendantsPaginated",
    queryVariables: (facilityId) => ({
      id: facilityId,
      assigned: false,
      deviceAssigned: true,
      type: "zone",
      subtype: "unit",
    }),
  },
  2: {
    itemsPerPage: unavailableItemsPerPage,
    loading: unavailableInventoryLoading,
    loaded: unavailableInventoryLoaded,
    observable: () => unavailableInventoryObservable,
    pagination: unavailablePaginationTracking,
    query: UNAVAILABLE_INVENTORY,
    targetArray: unavailableInventoryItems,
    dataKey: "unavailableItems",
    queryVariables: (facilityId) => ({
      id: facilityId,
      statuses: ["lost_contact", "ok"],
      availableInventory: false,
    }),
  },
};

const healthyFacilityDevices = computed(() =>
  facilityDevices.value.filter(
    (device) =>
      device.disposition === "in_service" &&
      device.status === "ok" &&
      !device.rmaRequest
  )
);

const unavailableFacilityDevices = computed(() =>
  facilityDevices.value.filter(
    (device) =>
      (device.disposition === "in_service" && device.status !== "ok") ||
      device.disposition !== "in_service" ||
      device.rmaRequest
  )
);

const loading = computed(() => {
  const config = TAB_CONFIGS[currentTab.value];
  return config
    ? config.loading.value
    : devicesLoading.value || unitsLoading.value;
});

const availableInventory = computed(() => {
  if (devicesLoading.value || unitsLoading.value) return [];

  const availableUnits = inventoryUnits.value.filter(unitWithAvailableDevices);
  return [
    ...devicesInUnits(endedServiceUnits, true),
    ...devicesInUnits(availableUnits),
    ...healthyFacilityDevices.value.map(deviceAsInventoryRow),
  ];
});

const unavailableInventory = computed(() => {
  if (devicesLoading.value || unitsLoading.value) return [];

  const unavailableUnits = inventoryUnits.value.filter(
    (unit) => !unitWithAvailableDevices(unit)
  );
  return [
    ...devicesInUnits(unavailableUnits),
    ...unavailableFacilityDevices.value.map(deviceAsInventoryRow),
  ];
});

const officeInventory = computed(() => {
  const devices = officeInventoryDevices.value || [];
  const healthyDevices = devices.filter(
    (device) =>
      device.disposition === "in_service" &&
      device.status === "ok" &&
      !device.rmaRequest
  );
  return healthyDevices.map(deviceAsInventoryRow);
});

const preinstalledInventory = computed(() => {
  if (preinstalledInventoryLoading.value) return [];

  return preinstalledInventoryUnits.value.map((unit) => ({
    ...unit,
    type: "unit",
    devices: unit.devices || [],
    devicesName: (unit.devices || []).map(formatDeviceName),
    devicesLastMotion: (unit.devices || []).map(deviceLastMotion),
    unitTypeName: displayUnitType(unit.unitType, "type"),
    unitTypeSize: displayUnitType(unit.unitType, "size"),
    isActiveDemo: isActiveDemoUnit(unit.responders),
  }));
});

const endedServiceInventoryRows = computed(() =>
  endedServiceUnits.value.flatMap((unit) =>
    (unit.devices || []).map((device) => ({
      ...deviceAsInventoryRow({ ...device, endedService: true }),
      name: unit.name,
      unitName: unit.name,
      devicesLastAssignedTo: [unit.name],
    }))
  )
);

const unavailableInventoryTab = computed(() => {
  const devices = unavailableInventoryItems.value || [];
  const allUnavailableDevices = devices.filter(
    (device) =>
      device.disposition !== "in_service" ||
      device.status !== "ok" ||
      device.rmaRequest
  );

  // endedServiceInventoryRows is already device-level (see deviceAsInventoryRow
  // above), not unit-level — each row's own id is the device id, there's no
  // nested `.devices` array to read.
  const endedServiceDeviceIds = new Set(
    endedServiceInventoryRows.value.map((row) => row.id)
  );
  const dedupedUnavailable = allUnavailableDevices.filter(
    (device) => !endedServiceDeviceIds.has(device.id)
  );

  // endedServiceInventoryRows is fetched in full, unpaginated, while
  // dedupedUnavailable is only the current server-paginated page — only
  // merge the ended-service rows into page 1 so they don't get repeated on
  // every subsequent page.
  const isFirstPage = unavailablePaginationTracking.value.currentPage === 1;

  return [
    ...(isFirstPage ? endedServiceInventoryRows.value : []),
    ...dedupedUnavailable.map(deviceAsInventoryRow),
  ];
});

function getApolloClient(apolloClient, observable = null) {
  if (apolloClient) return apolloClient;
  if (observable?.apolloClient) return observable.apolloClient;
  if (observable?.queryManager?.apolloClient)
    return observable.queryManager.apolloClient;

  if (
    typeof window !== "undefined" &&
    window.$apollo?.provider?.defaultClient
  ) {
    return window.$apollo.provider.defaultClient;
  }

  return null;
}

function executeDirectQuery(client, queryOptions) {
  if (!client) {
    return Promise.reject(new Error("Apollo client is required"));
  }

  return client.query({
    fetchPolicy: "network-only",
    ...queryOptions,
  });
}

const dataProcessors = {
  0: (data) => {
    const paginatedData = data.organizationDevicesPaginated;
    if (!paginatedData?.data) return { items: [], pageInfo: null };

    const items = paginatedData.data
      .map((device) => ({
        ...device,
        name:
          formatDeviceName(device) ||
          device.shortId ||
          device.externalId ||
          "-",
        lastMotion: deviceLastMotion(device),
      }))
      .sort((a, b) => stringSortCollator.compare(a.name, b.name));

    return { items, pageInfo: paginatedData.page };
  },
  1: (data) => {
    const paginatedData = data.organizationDescendantsPaginated;
    if (!paginatedData?.data) return { items: [], pageInfo: null };

    const items = paginatedData.data.sort((a, b) =>
      a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1
    );

    return { items, pageInfo: paginatedData.page };
  },
  2: (data) => {
    const paginatedData = data.unavailableItems;
    if (!paginatedData?.data) return { items: [], pageInfo: null };

    const items = paginatedData.data.map((device) => ({
      ...device,
      name:
        formatDeviceName(device) || device.shortId || device.externalId || "-",
      lastMotion: deviceLastMotion(device),
    }));

    return { items, pageInfo: paginatedData.page };
  },
};

function processQueryResult(data, tabIndex) {
  const processor = dataProcessors[tabIndex];
  return processor ? processor(data) : { items: [], pageInfo: null };
}

function updatePaginationTracking(tabIndex, pageInfo) {
  const config = TAB_CONFIGS[tabIndex];
  if (!config || !pageInfo) return;

  const pagination = config.pagination.value;

  pagination.currentPage = pageInfo.currentPage;
  pagination.totalPages = pageInfo.totalPages;
  pagination.totalCount = pageInfo.totalCount;
  pagination.pageSize = pageInfo.pageSize;
  pagination.hasNextPage = pageInfo.hasNextPage;
  pagination.hasPreviousPage = pageInfo.hasPreviousPage;

  pagination.hasMore.older = pageInfo.hasNextPage;
  pagination.hasMore.newer = pageInfo.hasPreviousPage;
}

function cleanUpInventoryRefs() {
  inventoryUnits.value = [];
  endedServiceUnits.value = [];
  facilityDevices.value = [];
  officeInventoryDevices.value = [];
  preinstalledInventoryUnits.value = [];
  unavailableInventoryItems.value = [];

  devicesLoading.value = true;
  unitsLoading.value = true;
  endedServiceUnitsLoading.value = true;
  officeInventoryLoading.value = true;
  preinstalledInventoryLoading.value = true;
  unavailableInventoryLoading.value = true;

  officeInventoryLoaded.value = false;
  preinstalledInventoryLoaded.value = false;
  unavailableInventoryLoaded.value = false;
  originalTotalCounts.value = {
    0: 0,
    1: 0,
    2: 0,
  };

  devicesObservable = null;
  unitsObservable = null;
  endedServiceObservable = null;
  officeInventoryObservable = null;
  preinstalledInventoryObservable = null;
  unavailableInventoryObservable = null;

  officePaginationTracking.value = createPaginationState();
  preinstalledPaginationTracking.value = createPaginationState();
  unavailablePaginationTracking.value = createPaginationState();
}

export default function useInventory() {
  const { currentFacilityId } = useFacility();

  function createQueryVariables(
    tabIndex,
    itemsPerPage,
    searchText = "",
    facilityIdOverride = null
  ) {
    const config = TAB_CONFIGS[tabIndex];
    if (!config) return {};

    const facilityId = facilityIdOverride || currentFacilityId.value;
    const variables = config.queryVariables(facilityId);

    if (searchText) {
      variables.searchText = searchText;
      const originalCount = originalTotalCounts.value[tabIndex];
      const currentCount = config.pagination.value.totalCount;

      if (originalCount > 0) {
        variables.limit = originalCount;
      } else if (currentCount > 0) {
        variables.limit = currentCount;
      } else {
        variables.limit = 99999;
      }
      variables.page = 1;
    } else if (itemsPerPage === -1) {
      const totalCount = config.pagination.value.totalCount;
      variables.limit = totalCount < 1 ? 99999 : totalCount;
      variables.page = 1;
    } else if (itemsPerPage > 0) {
      variables.limit = itemsPerPage;
      variables.page = config.pagination.value.currentPage;
    }

    return variables;
  }

  function fetchTabData(
    tabIndex,
    apolloClient,
    itemsPerPage,
    searchText = "",
    facilityIdOverride = null
  ) {
    const config = TAB_CONFIGS[tabIndex];
    if (!config || !apolloClient) {
      return null;
    }
    if (!searchText) {
      originalTotalCounts.value[tabIndex] = 0;
    }

    if (
      !searchText &&
      config.pagination.value.totalCount > 0 &&
      originalTotalCounts.value[tabIndex] === 0
    ) {
      originalTotalCounts.value[tabIndex] = config.pagination.value.totalCount;
    }

    if (currentTab.value !== tabIndex) {
      const prevConfig = TAB_CONFIGS[currentTab.value];
      if (prevConfig?.observable()?.stopPolling) {
        prevConfig.observable().stopPolling();
      }
      if (prevConfig) {
        prevConfig.itemsPerPage.value = 10;
      }
    }

    currentTab.value = tabIndex;

    if (itemsPerPage !== undefined) {
      config.itemsPerPage.value = itemsPerPage;
    }

    if (searchText === "") {
      config.loaded.value = false;
    }

    if (!config.loaded.value || searchText !== "") {
      config.loaded.value = true;
      return fetchInventoryData(
        tabIndex,
        apolloClient,
        config.itemsPerPage.value,
        searchText,
        facilityIdOverride
      );
    } else if (currentTab.value !== tabIndex) {
      return config.observable()?.refetch();
    }

    return config.observable();
  }
  function fetchInventoryData(
    tabIndex,
    apolloClient,
    itemsPerPage = 10,
    searchText = "",
    facilityIdOverride = null
  ) {
    const config = TAB_CONFIGS[tabIndex];
    if (!config) return null;

    config.loading.value = true;
    if (
      searchText &&
      originalTotalCounts.value[tabIndex] === 0 &&
      config.pagination.value.totalCount > 0
    ) {
      originalTotalCounts.value[tabIndex] = config.pagination.value.totalCount;
    }

    let effectiveItemsPerPage = itemsPerPage;
    if (searchText) {
      const originalCount = originalTotalCounts.value[tabIndex];
      effectiveItemsPerPage =
        originalCount > 0 ? originalCount : config.pagination.value.totalCount;
    }

    const variables = createQueryVariables(
      tabIndex,
      effectiveItemsPerPage,
      searchText,
      facilityIdOverride
    );

    const observable = apolloWatchQuery(
      apolloClient,
      {
        query: config.query,
        variables,
        fetchPolicy: "network-only",
      },
      (data, loading) => {
        config.loading.value = loading;
        if (data) {
          const { items, pageInfo } = processQueryResult(data, tabIndex);
          config.targetArray.value = items;

          if (pageInfo) {
            if (!searchText) {
              updatePaginationTracking(tabIndex, pageInfo);

              if (
                pageInfo.totalCount > 0 &&
                originalTotalCounts.value[tabIndex] === 0
              ) {
                originalTotalCounts.value[tabIndex] = pageInfo.totalCount;
              }
            } else {
              const pagination = config.pagination.value;
              pagination.currentPage = 1;
              pagination.hasNextPage = false;
              pagination.hasPreviousPage = false;
              pagination.hasMore.older = false;
              pagination.hasMore.newer = false;
            }
          }
        }
      }
    );

    switch (tabIndex) {
      case 0:
        officeInventoryObservable = observable;
        fetchInventoryUnits(apolloClient);
        break;
      case 1:
        preinstalledInventoryObservable = observable;
        if (inventoryUnits.value.length === 0) {
          fetchInventoryUnits(apolloClient);
        }
        break;
      case 2:
        unavailableInventoryObservable = observable;
        break;
    }

    return observable;
  }

  function fetchInventoryUnits(apolloClient) {
    apolloWatchQuery(
      apolloClient,
      {
        query: INVENTORY_SMART_UNITS,
        variables: {
          id: currentFacilityId.value,
          availableInventory: true,
        },
        fetchPolicy: "network-only",
      },
      (data) => {
        if (data?.inventorySmartUnits) {
          inventoryUnits.value = data.inventorySmartUnits.sort((a, b) =>
            a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1
          );
        }
      }
    );
  }

  function fetchFacilityDevices(apolloClient) {
    devicesObservable = apolloWatchQuery(
      apolloClient,
      {
        query: ORGANIZATION_DEVICES,
        variables: {
          id: currentFacilityId.value,
          dispositions: [
            "missing",
            "shipping",
            "under_maintenance",
            "retired",
            "in_service",
            "waiting",
          ],
        },
        fetchPolicy: "network-only",
      },
      (data, loading) => {
        devicesLoading.value = loading;
        if (data?.organizationDevices) {
          facilityDevices.value = data.organizationDevices
            .map((device) => ({
              ...device,
              name: formatDeviceName(device),
              lastMotion: deviceLastMotion(device),
            }))
            .sort((a, b) => stringSortCollator.compare(a.name, b.name));
        }
      }
    );
    return devicesObservable;
  }

  function fetchInventory(apolloClient) {
    fetchFacilityDevices(apolloClient);

    unitsObservable = apolloWatchQuery(
      apolloClient,
      {
        query: INVENTORY_SMART_UNITS,
        variables: { id: currentFacilityId.value },
        fetchPolicy: "network-only",
      },
      (data, loading) => {
        unitsLoading.value = loading;
        if (data?.inventorySmartUnits) {
          inventoryUnits.value = data.inventorySmartUnits.sort((a, b) =>
            a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1
          );
        }
      }
    );

    return [devicesObservable, unitsObservable];
  }

  function fetchEndedServiceUnits(apolloClient) {
    endedServiceObservable = apolloWatchQuery(
      apolloClient,
      {
        query: ENDED_SERVICE_SMART_UNITS,
        variables: { id: currentFacilityId.value },
        fetchPolicy: "network-only",
      },
      (data, loading) => {
        endedServiceUnitsLoading.value = loading;
        if (data?.endedServiceSmartUnits) {
          endedServiceUnits.value = data.endedServiceSmartUnits.sort((a, b) =>
            a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1
          );
        }
      }
    );
    return endedServiceObservable;
  }

  function refetchInventory() {
    [
      devicesObservable,
      unitsObservable,
      endedServiceObservable,
      officeInventoryObservable,
      preinstalledInventoryObservable,
      unavailableInventoryObservable,
    ].forEach((observable) => observable?.refetch());
  }

  function navigatePage(tabIndex, direction) {
    const config = TAB_CONFIGS[tabIndex];
    if (!config) return;

    const pagination = config.pagination.value;
    const observable = config.observable();

    if (direction === "next" && !pagination.hasNextPage) {
      return;
    }

    if (direction === "previous" && !pagination.hasPreviousPage) {
      return;
    }

    config.loading.value = true;

    const newPage =
      direction === "next"
        ? pagination.currentPage + 1
        : pagination.currentPage - 1;

    pagination.currentPage = newPage;

    if (observable) {
      const variables = createQueryVariables(
        tabIndex,
        config.itemsPerPage.value
      );

      variables.page = newPage;

      observable
        .refetch(variables)
        .catch((error) => {
          pagination.currentPage =
            direction === "next"
              ? pagination.currentPage - 1
              : pagination.currentPage + 1;
        })
        .finally(() => {
          config.loading.value = false;
        });
    } else {
      config.loading.value = false;
    }
  }

  function loadNextPage(tabIndex) {
    navigatePage(tabIndex, "next");
  }

  function loadPreviousPage(tabIndex) {
    navigatePage(tabIndex, "previous");
  }

  function resetPagination(tabIndex) {
    const config = TAB_CONFIGS[tabIndex];
    if (!config) return;

    config.pagination.value = {
      ...createPaginationState(),
      pageSize: config.itemsPerPage.value,
    };

    const observable = config.observable();
    if (observable?.options?.variables) {
      observable.options.variables.page = 1;
    }
  }

  function updateItemsPerPage(tabIndex, itemsPerPage, apolloClient) {
    if (!apolloClient) {
      throw new Error("Apollo client is required for updateItemsPerPage");
    }
    if (typeof tabIndex !== "number" || tabIndex < 0 || tabIndex > 2) {
      throw new Error("tabIndex must be a number between 0 and 2");
    }
    if (typeof itemsPerPage !== "number" || itemsPerPage < -1) {
      throw new Error("itemsPerPage must be a number >= -1");
    }

    const config = TAB_CONFIGS[tabIndex];
    if (!config) {
      return Promise.reject(new Error(`Invalid tab index: ${tabIndex}`));
    }

    const originalItemsPerPage = config.itemsPerPage.value;
    const originalPagination = { ...config.pagination.value };

    const observable = config.observable();
    if (!observable) {
      config.loading.value = false;
      return Promise.resolve();
    }

    config.loading.value = true;

    if (itemsPerPage === -1) {
      const totalCount = config.pagination.value.totalCount;
      const variables = createQueryVariables(tabIndex, itemsPerPage);

      variables.limit = totalCount;
      variables.page = 1;

      return observable
        .refetch(variables)
        .then((result) => {
          if (result.data) {
            const { items, pageInfo } = processQueryResult(
              result.data,
              tabIndex
            );
            config.targetArray.value = items;

            const pagination = config.pagination.value;
            pagination.currentPage = 1;
            pagination.hasNextPage = false;
            pagination.hasPreviousPage = false;
            pagination.hasMore.older = false;
            pagination.hasMore.newer = false;

            if (pageInfo) {
              pagination.totalCount = pageInfo.totalCount;
              pagination.pageSize = pageInfo.totalCount;
            }
          }
          return result;
        })
        .catch((error) => {
          config.itemsPerPage.value = originalItemsPerPage;
          config.pagination.value = originalPagination;
          throw error;
        })
        .finally(() => {
          config.loading.value = false;
        });
    } else {
      config.itemsPerPage.value = itemsPerPage;
      resetPagination(tabIndex);

      const variables = createQueryVariables(tabIndex, itemsPerPage);

      return observable
        .refetch(variables)
        .catch((error) => {
          config.itemsPerPage.value = originalItemsPerPage;
          config.pagination.value = originalPagination;
          throw error;
        })
        .finally(() => {
          config.loading.value = false;
        });
    }
  }

  function fetchTabDataWithSearch(tabIndex, apolloClient, searchText = "") {
    const config = TAB_CONFIGS[tabIndex];
    if (!config || !apolloClient) {
      return null;
    }

    config.loading.value = true;

    if (
      searchText &&
      originalTotalCounts.value[tabIndex] === 0 &&
      config.pagination.value.totalCount > 0
    ) {
      originalTotalCounts.value[tabIndex] = config.pagination.value.totalCount;
    }

    const totalCount =
      searchText && originalTotalCounts.value[tabIndex] > 0
        ? originalTotalCounts.value[tabIndex]
        : config.pagination.value.totalCount;

    const variables = createQueryVariables(tabIndex, 10);

    if (searchText) {
      variables.searchText = searchText;
      variables.limit =
        totalCount > 0
          ? totalCount
          : config.pagination.value.totalCount || 99999;
      variables.page = 1;
    }

    const observable = apolloWatchQuery(
      apolloClient,
      {
        query: config.query,
        variables,
        fetchPolicy: "network-only",
      },
      (data, loading) => {
        config.loading.value = loading;
        if (data) {
          const { items, pageInfo } = processQueryResult(data, tabIndex);
          config.targetArray.value = items;

          if (pageInfo) {
            if (!searchText) {
              updatePaginationTracking(tabIndex, pageInfo);
              if (pageInfo.totalCount > 0) {
                originalTotalCounts.value[tabIndex] = pageInfo.totalCount;
              }
            } else {
              const pagination = config.pagination.value;
              pagination.currentPage = 1;
              pagination.hasNextPage = false;
              pagination.hasPreviousPage = false;
              pagination.hasMore.older = false;
              pagination.hasMore.newer = false;
            }
          }
        }
      }
    );

    switch (tabIndex) {
      case 0:
        officeInventoryObservable = observable;
        break;
      case 1:
        preinstalledInventoryObservable = observable;
        if (inventoryUnits.value.length === 0) {
          fetchInventoryUnits(apolloClient);
        }
        break;
      case 2:
        unavailableInventoryObservable = observable;
        break;
    }

    return observable;
  }

  return {
    devicesLoading,
    loading,
    inventoryUnits: readonly(inventoryUnits),
    healthyFacilityDevices,
    unavailableFacilityDevices,
    availableInventory,
    unavailableInventory,
    endedServiceUnits: readonly(endedServiceUnits),
    endedServiceUnitsLoading: readonly(endedServiceUnitsLoading),
    officeInventory,
    preinstalledInventory,
    unavailableInventoryTab,
    endedServiceInventoryRows,
    unavailableInventoryItems: readonly(unavailableInventoryItems),
    officePaginationTracking: readonly(officePaginationTracking),
    preinstalledPaginationTracking: readonly(preinstalledPaginationTracking),
    unavailablePaginationTracking: readonly(unavailablePaginationTracking),
    officeItemsPerPage: readonly(officeItemsPerPage),
    preinstalledItemsPerPage: readonly(preinstalledItemsPerPage),
    unavailableItemsPerPage: readonly(unavailableItemsPerPage),
    fetchFacilityDevices,
    fetchInventory,
    fetchEndedServiceUnits,
    fetchTabData,
    fetchTabDataWithSearch,
    refetchInventory,
    cleanUpInventoryRefs,
    loadNextPage,
    loadPreviousPage,
    resetPagination,
    updateItemsPerPage,
    formatDeviceName,
  };
}

function devicesInUnits(inventoryUnits, areEndedServiceUnits = false) {
  return unref(inventoryUnits).reduce((acc, unit) => {
    if (unit.devices?.length > 0) {
      const responder =
        areEndedServiceUnits && unit.responders?.length > 0
          ? { ...unit.responders[0], status: "ended_service" }
          : demoResponder(unit);

      acc.push({
        ...unit,
        ...extractDevicesFields(unit.devices),
        type: "unit",
        unitTypeName: displayUnitType(unit?.unitType, "type"),
        unitTypeSize: displayUnitType(unit?.unitType, "size"),
        responder,
        endedServiceResponder:
          areEndedServiceUnits &&
          unit.responders?.length > 0 &&
          unit.responders[0],
      });
    }
    return acc;
  }, []);
}

function deviceAsInventoryRow(device) {
  return {
    ...extractDevicesFields(device),
    id: device.id,
    name: " - ",
    type: "device",
    unitTypeName: "",
  };
}
